import { useEffect, useMemo, useRef } from 'react';
import type { StreamChat } from 'stream-chat';

import { Button } from '../components/Button.js';
import { Icon } from '../components/Icon.js';
import {
  formatDuration,
  formatEventDate,
  formatTimeRange,
  plural,
  toTimeInput,
} from '../util/format.js';
import { useDismiss } from '../util/useDismiss.js';
import { DateField, TimeField } from './DateTimeFields.js';
import { draftSpan, draftStart, missing, SPANS } from './draft.js';
import { durationMinutes, minutesAfter } from './event-rules.js';
import { EventMessageField } from './EventMessageField.js';
import { EventTitleField } from './EventTitleField.js';
import { GuestList } from './GuestList.js';
import { GuestPicker } from './GuestPicker.js';
import type { BookingFailure } from './useBooking.js';
import type { ChatDayEditor } from './useChatDay.js';
import styles from './ChatDayDialog.module.css';
import type { EventDraft } from './draft.js';

interface ChatDayDialogProps {
  editor: ChatDayEditor;
  client: StreamChat | null;
  /** The booking is in flight. See `useBooking` for why this cannot be late. */
  saving: boolean;
  /** Why the last press did not book, or null. */
  error: BookingFailure | null;
  /**
   * Save. The covering note travels beside the draft rather than on it: it is
   * not part of the record, it becomes its own message in the guests' chats,
   * and putting it on `EventDraft` would make every rule about the event have
   * to say "except this field".
   */
  onSave: (draft: EventDraft, note: string) => void;
}

/**
 * Booking a Chat Day.
 *
 * A centred modal with two panes: the block of time on the left, the people on
 * the right. The left pane is the event popover at its exact width — same
 * fields, same order, same 440px — because it is the same form. What the modal
 * adds is the second pane, and that is the whole reason it is a modal at all:
 * booking one interview is a ten-second errand best done beside the slot you
 * clicked, and building a guest list of twenty-four is not, and will not fit
 * there.
 *
 * So: everything on the left is a short answer about a window of time;
 * everything on the right is the decision. Stacking them into one column would
 * bury the guests under the fields and make the pane that matters the one you
 * have to scroll to.
 *
 * **No Where field.** An interview needs one; a Chat Day is *held in the chats*
 * by definition — that is the idea — so a venue row would be a field that can
 * only ever be answered wrongly. `missing` exempts it.
 *
 * **No reminders field.** calendar-service drives reminders from
 * `user_settings.reminder_config`, per user, so a per-event reminder has nowhere
 * to be stored.
 *
 * Edit mode (`state.editing`, AI-3215) only adds guests to a saved event:
 * everything else is read-only, and the saved guests cannot be removed.
 */
export function ChatDayDialog({ editor, client, saving, error, onSave }: ChatDayDialogProps) {
  const state = editor.state;
  // Not while it is saving. Escape here would close a modal whose write is still
  // in the air, leaving the employer with no way to learn how it went — and the
  // draft is gone with it, because there is no draft between bookings.
  useDismiss(state !== null && !saving, editor.close);

  /**
   * The press on Save goes to the first field it is waiting for.
   *
   * Needed here more than on a popover: this modal is two columns and scrolls,
   * so a mark on a control below the fold is a press that recoloured something
   * nobody can see.
   */
  const bodyRef = useRef<HTMLDivElement>(null);
  const wasShowing = useRef(false);
  const gapsShown = state?.gapsShown ?? false;
  const invited = state?.invited;
  const alreadyInvited = useMemo(() => new Set(invited?.map((person) => person.id)), [invited]);
  useEffect(() => {
    if (!gapsShown) {
      wasShowing.current = false;
      return;
    }
    if (wasShowing.current) return;
    wasShowing.current = true;
    const first = bodyRef.current?.querySelector<HTMLElement>('[data-gap]');
    first?.scrollIntoView({ block: 'center' });
    first?.focus();
  }, [gapsShown]);

  if (!state) return null;

  const { draft, editing } = state;
  const gaps = missing(draft);
  const shown = state.gapsShown ? gaps : [];
  const span = editing ? { startsAt: editing.startsAt, endsAt: editing.endsAt } : draftSpan(draft);
  const held = span ? durationMinutes(span) : null;

  /** Moving the start takes the end with it, keeping the length — the commonest
   *  edit on any of these forms is "same thing, an hour later". */
  const moveStart = (next: string) => {
    const moved = draftStart({ ...draft, from: next });
    editor.patch({
      from: next,
      ...(moved ? { to: toTimeInput(minutesAfter(moved, held ?? SPANS[0]!.minutes)) } : {}),
    });
  };

  const setLength = (minutes: number) => {
    const starts = draftStart(draft);
    if (!starts) return;
    editor.patch({ to: toTimeInput(minutesAfter(starts, minutes)) });
  };

  /**
   * Save is never disabled; pressing it on an incomplete draft marks what is
   * missing. A greyed button would be a dead control with no word about which
   * field it is waiting for — and most of the fields here are required, so it
   * would be greyed for most of the time the form is open.
   */
  const commit = () => {
    if (saving) return;
    if (editing) {
      if (draft.invitees.length > 0) onSave(draft, state.message);
      return;
    }
    if (gaps.length > 0) {
      editor.showGaps();
      return;
    }
    onSave(draft, state.message);
  };

  const saveLabel = saving
    ? editing
      ? 'Saving…'
      : 'Sending…'
    : editing
      ? 'Save changes'
      : draft.invitees.length > 0
        ? `Send to ${plural(draft.invitees.length, 'guest')}`
        : 'Send invites';

  return (
    <div className={styles.overlay} onMouseDown={() => !saving && editor.close()}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={editing ? `Add guests to ${editing.title}` : 'New Chat Day'}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={styles.body} ref={bodyRef}>
          <div className={styles.form}>
            {editing && <span className={styles.badge}>Editing</span>}
            <fieldset className={styles.locked} disabled={editing !== null}>
              {/*
               * The title leads the column, exactly as it leads the popover. What
               * names the *kind* is the placeholder, which is already this field's
               * label and leaves the moment it is answered; an eyebrow over it
               * would be two titles, one of them uneditable.
               *
               * No close button beside it, and none anywhere. Cancel closes this,
               * and so do Escape and a press on the scrim — a cross in the corner
               * would be a fourth way to do what the footer already offers in
               * words, and it is the one that sits closest to a text field, where
               * a cross means "clear what I typed".
               */}
              <EventTitleField
                value={draft.title}
                gap={shown.includes('title')}
                placeholder="Name this Chat Day"
                onChange={(title) => editor.patch({ title })}
              />

              <section className={styles.section}>
                <p
                  className={shown.includes('when') ? styles.labelGap : styles.label}
                  data-gap={shown.includes('when') || undefined}
                  tabIndex={-1}
                >
                  When
                </p>
                <div className={styles.when}>
                  <DateField value={draft.date} onChange={(date) => editor.patch({ date })} />
                  <span className={styles.clocks}>
                    <TimeField value={draft.from} label="Start time" onChange={moveStart} />
                    <span className={styles.dash}>–</span>
                    <TimeField
                      value={draft.to}
                      label="End time"
                      after={draft.from}
                      onChange={(to) => editor.patch({ to })}
                    />
                  </span>
                </div>

                <div className={styles.spans}>
                  {SPANS.map((option) => (
                    <button
                      key={option.minutes}
                      type="button"
                      className={held === option.minutes ? styles.spanOn : styles.span}
                      aria-pressed={held === option.minutes}
                      onClick={() => setLength(option.minutes)}
                    >
                      {option.label}
                    </button>
                  ))}
                  {/* The length in words beside the pills, because a Chat Day is
                    routinely a span none of the three name — dragged to 09:00 to
                    17:00, the pills all go quiet and this is what says how long
                    it runs. */}
                  {held !== null && !SPANS.some((option) => option.minutes === held) && (
                    <span className={styles.spanCustom}>{formatDuration(held)}</span>
                  )}
                </div>
              </section>
            </fieldset>

            {/* Where the event form puts its single invitee. Adding somebody in
                the picker beside this fills this field — that pane is the
                drawer, this is the record. */}
            <GuestList
              invitees={draft.invitees}
              invited={state.invited}
              gap={shown.includes('invitees')}
              onRemove={editor.removeGuest}
            />

            <fieldset className={styles.locked} disabled={editing !== null}>
              <section className={styles.section}>
                <p className={styles.label}>Description</p>
                <textarea
                  className={styles.textarea}
                  value={draft.description}
                  placeholder="What to expect, what to bring, how long each chat will take"
                  aria-label="Description"
                  onChange={(event) => editor.patch({ description: event.target.value })}
                />
              </section>
            </fieldset>

            <section className={styles.section}>
              {state.messageOpen ? (
                <>
                  <p className={styles.label}>Message</p>
                  <EventMessageField
                    value={state.message}
                    onChange={editor.setMessage}
                    onRemove={editor.dropMessage}
                  />
                </>
              ) : (
                <button type="button" className={styles.addMessage} onClick={editor.openMessage}>
                  <Icon name="plus" size={16} />
                  Add message
                </button>
              )}
            </section>
          </div>

          <GuestPicker
            invitees={draft.invitees}
            alreadyInvited={alreadyInvited}
            client={client}
            onAdd={editor.addGuests}
          />
        </div>

        <div className={styles.footer}>
          {/*
           * One line to the left of the buttons, and what it says depends on
           * whether the last press worked.
           *
           * Normally the when and the how-many, together at the moment of
           * pressing Save: both are already on the form, but in two places
           * twenty centimetres apart — and the guest count in particular is over
           * in the other pane, which is exactly where the eye is not when the
           * hand is on the button.
           *
           * After a failure, the reason takes that place rather than crowding in
           * beside it. The summary restates what is already on screen; the
           * failure is the only thing here the employer does not already know,
           * and a footer holding both puts them in a fight for the same room.
           *
           * Either way it is beside the button that caused it, not in a toast
           * over the calendar: the calendar is behind a scrim, the thing to
           * change is on this form, and a message that outlived the modal would
           * still be on screen after Cancel.
           */}
          {error ? (
            <p className={styles.error} role="alert">
              {error.message}
              {/* `retryable` decides whether it invites another press. A calendar
                  that was briefly unreachable is worth pressing Save again; a
                  time that has since been taken is not, and "try again" there
                  would be an instruction to do the one thing that cannot work. */}
              {error.retryable && ' Try again.'}
            </p>
          ) : (
            span && (
              <p className={styles.summary}>
                {formatEventDate(span.startsAt)} · {formatTimeRange(span.startsAt, span.endsAt)}
                {held !== null && ` · ${formatDuration(held)}`}
              </p>
            )
          )}
          <Button variant="ghost" onClick={editor.close} disabled={saving}>
            Cancel
          </Button>
          {/* The count is on the button because it is the thing that goes wrong:
              a Chat Day is saved once and reaches everybody at once, and "Save"
              alone would be the same word for two guests and for forty.

              Disabled only while the write is in the air, and that is not the
              dead-control case above: it says "this is happening", it lasts a
              moment, and it is the only thing standing between one press and two
              Chat Days that cannot be unbooked. */}
          <Button
            onClick={commit}
            disabled={saving || (editing !== null && draft.invitees.length === 0)}
            aria-busy={saving || undefined}
          >
            {saveLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
